import React, { useState, useEffect } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import { 
  IconButton, 
  Box, 
  AppBar, 
  Toolbar, 
  Dialog, 
  DialogContent, 
  useMediaQuery,
  Popover,
  Badge,
} from '@mui/material'
import { HeaderContainer } from './ui/StyledComponents'
import Settings from './Settings'
import Search from './Search'
// import MailIcon from '@mui/icons-material/Mail'
// import supabase from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import MenuIcon from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'
import NotificationsIcon from '@mui/icons-material/Notifications'
import madrasatiLightLogo from '../assets/madrasati-light.svg'
import madrasatiDarkLogo from '../assets/madrasati-dark.svg'
import AlertsPanel from './admin/AlertsPanel'
import useAdminAlerts from '../hooks/useAdminAlerts'

// Home button - updated for a more elegant look
const HomeButton = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  textDecoration: 'none',
  padding: theme.spacing(0.5, 1),
  marginRight: theme.spacing(2),
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderBottom: '2px solid transparent',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25, 0.5),
    marginRight: 0,
  },
  '&:hover': {
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
}))

// Logo image styled component
const LogoImage = styled('img')(({ theme }) => ({
  height: '48px',
  width: 'auto',
  transition: 'opacity 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    height: '36px', // Smaller logo on mobile
  },
  '&:hover': {
    opacity: 0.8,
  },
}))

// Styled icon button for consistent header icons with outline
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
  transition: 'all 0.2s ease',
  padding: theme.spacing(1),
  border: `1px solid rgba(255, 255, 255, 0.2)`,
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'translateY(-2px)',
  },
}))

function Header({ toggleDrawer }) {
  // Comment out messaging state
  // const [unreadCount, setUnreadCount] = useState(0)
  const theme = useTheme()
  const { user, userRole } = useAuth()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null)
  
  // Only fetch alerts if user is admin
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'
  const { alerts, loading: alertsLoading } = useAdminAlerts()
  
  // Filter out dismissed alerts for badge count
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set())
  
  useEffect(() => {
    if (isAdmin) {
      const stored = localStorage.getItem('dismissed_alerts')
      if (stored) {
        try {
          const dismissed = JSON.parse(stored)
          setDismissedAlerts(new Set(dismissed))
        } catch (e) {
          console.error('Error loading dismissed alerts:', e)
        }
      }
    }
  }, [isAdmin])
  
  const visibleAlertsCount = alerts.filter((alert) => !dismissedAlerts.has(alert.id)).length
  
  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget)
  }
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null)
  }
  
  const handleAlertDismissed = (newDismissedSet) => {
    setDismissedAlerts(newDismissedSet)
  }
  
  const notificationsOpen = Boolean(notificationsAnchorEl)

  // Comment out messaging effects
  /* 
  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('is_read', false)
        .neq('sender_id', user.id);

      if (!error) {
        setUnreadCount(data.length);
      }
    };

    fetchUnreadCount();

    const subscription = supabase
      .channel('unread_messages')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
      }, () => {
        fetchUnreadCount()
      })
      .subscribe()
      
    return () => {
      supabase.removeChannel(subscription)
    }
  }, [user])
  */

  // Don't render header if user is not logged in
  if (!user) {
    return null;
  }

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{ zIndex: 1300, backgroundColor: theme.palette.secondary.main, borderRadius: 0 }}
    >
      <Toolbar 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 2, sm: 2 },
          gap: { xs: 1, sm: 2 },
        }}
      >
        <HeaderContainer 
          sx={{ 
            width: '100%', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: 0, sm: 0 }, // Remove padding, let Toolbar handle it
            gap: { xs: 0.5, sm: 2 },
          }}
        >
          {/* Left side - navigation elements */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexShrink: 0,
              minWidth: 0, // Allow shrinking
            }}
          >
            <StyledIconButton
              aria-label="open drawer"
              onClick={toggleDrawer}
              edge="start"
              sx={{ 
                ml: { xs: 0, sm: 0 }, // Add spacing from left edge
                mr: { xs: 0.5, sm: 2 },
                padding: { xs: 0.5, sm: 1 },
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </StyledIconButton>
            <HomeButton to={user?.isAdmin ? '/admin' : '/'}>
              <LogoImage
                src={theme.palette.mode === 'dark' ? madrasatiDarkLogo : madrasatiLightLogo}
                alt="Madrasati Logo"
              />
            </HomeButton>
          </Box>

          {/* Center - Search */}
          <Box 
            sx={{ 
              display: { xs: 'none', sm: 'flex' }, // Hide on very small screens, show on tablet+
              alignItems: 'center', 
              flex: 1,
              justifyContent: 'center',
              minWidth: 0, // Allow shrinking
              mx: { xs: 1, sm: 2 },
              maxWidth: { xs: '100%', sm: 'none' },
            }}
          >
            <Search />
          </Box>

          {/* Right side - user controls */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexShrink: 0,
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {/* Mobile Search Icon */}
            {isMobile && ['admin', 'superadmin', 'teacher'].includes(userRole) && (
              <StyledIconButton
                aria-label="search"
                onClick={() => setSearchDialogOpen(true)}
                sx={{ 
                  padding: { xs: 0.5, sm: 1 },
                }}
              >
                <SearchIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </StyledIconButton>
            )}
            {/* Notifications Icon - Admin Only */}
            {isAdmin && (
              <StyledIconButton
                aria-label="notifications"
                onClick={handleNotificationsClick}
                sx={{ 
                  padding: { xs: 0.5, sm: 1 },
                }}
              >
                <Badge 
                  badgeContent={visibleAlertsCount} 
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: '18px',
                      minWidth: '18px',
                      padding: '0 4px',
                    },
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                </Badge>
              </StyledIconButton>
            )}
            <Settings userRole={userRole} />
          </Box>
        </HeaderContainer>
      </Toolbar>

      {/* Mobile Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        fullScreen={false}
        PaperProps={{
          sx: {
            position: 'fixed',
            top: { xs: '56px', sm: '64px' }, // Start below header
            left: 0,
            right: 0,
            bottom: 'auto',
            maxHeight: { xs: '50vh', sm: 'none' }, // Top half on mobile
            height: { xs: '50vh', sm: 'auto' },
            margin: 0,
            borderRadius: { xs: '0 0 16px 16px', sm: 2 },
            maxWidth: { xs: '100%', sm: '600px' },
            width: { xs: '100%', sm: 'auto' },
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogContent 
          sx={{ 
            p: { xs: 2, sm: 2 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible', // Allow dropdown to overflow
            position: 'relative',
            '&.MuiDialogContent-root': {
              overflow: 'visible',
            },
          }}
        >
          <Search onSelect={() => setSearchDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Notifications Popover */}
      {isAdmin && (
        <Popover
          open={notificationsOpen}
          anchorEl={notificationsAnchorEl}
          onClose={handleNotificationsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              width: { xs: '90vw', sm: '400px', md: '450px' },
              maxWidth: '90vw',
              maxHeight: { xs: '70vh', sm: '600px' },
              overflow: 'auto',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <AlertsPanel 
              alerts={alerts}
              loading={alertsLoading}
              showWhenEmpty={true}
              onAlertDismissed={handleAlertDismissed}
              onClose={handleNotificationsClose}
            />
          </Box>
        </Popover>
      )}
    </AppBar>
  )
}

export default Header
